import React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { KoobMenu, KoobMenuItem } from './KoobMenu';
import {
  resolveTableActionIcon,
  tableActionVariants,
  type KoobTableActionIcon,
} from './KoobTableAction';

/** KoobMenuItem pre-bound with stopPropagation, since table action menus
 *  live inside clickable rows. Kept as a named export for back-compat. */
export const KoobTableActionMenuItem = React.forwardRef<
  React.ElementRef<typeof KoobMenuItem>,
  Omit<React.ComponentProps<typeof KoobMenuItem>, 'stopPropagation'>
>((props, ref) => <KoobMenuItem ref={ref} stopPropagation {...props} />);
KoobTableActionMenuItem.displayName = 'KoobTableActionMenuItem';

type KoobTableActionMenuProps = {
  /** Trigger icon: a KoobTableAction preset name or a custom node. */
  icon?: KoobTableActionIcon | React.ReactNode;
  /** Accessible name of the trigger. */
  label: string;
  /** Forwarded to the trigger button (e2e selectors). */
  id?: string;
  disabled?: boolean;
  /** KoobTableActionMenuItem elements. */
  children: React.ReactNode;
  'data-testid'?: string;
};

/** A KoobTableAction-looking trigger that opens a small menu — for rows
 *  whose actions don't fit as inline icons. */
const KoobTableActionMenu = React.forwardRef<
  HTMLButtonElement,
  KoobTableActionMenuProps
>(({ icon = 'menu', label, id, disabled, children, ...props }, ref) => (
  <PopoverPrimitive.Root>
    <PopoverPrimitive.Trigger asChild>
      <button
        ref={ref}
        type="button"
        id={id}
        aria-label={label}
        disabled={disabled}
        className={tableActionVariants({ variant: 'default' })}
        onClick={(event) => event.stopPropagation()}
        {...props}
      >
        {icon === 'menu' ? (
          <i className="fa-regular fa-ellipsis-vertical" />
        ) : (
          resolveTableActionIcon(icon)
        )}
      </button>
    </PopoverPrimitive.Trigger>
    <KoobMenu onClick={(event) => event.stopPropagation()}>{children}</KoobMenu>
  </PopoverPrimitive.Root>
));
KoobTableActionMenu.displayName = 'KoobTableActionMenu';

export default KoobTableActionMenu;
